// frontend/src/marketing/pages/PayReturnPage.tsx
//
// Public, unauthenticated landing pages for the Stripe Checkout return trip.
//
// `createPayLinkForInvoice` (api/modules/payments/checkout.service.ts) sends
// Stripe `success_url = {APP}/pay/success?invoice=…` and
// `cancel_url = {APP}/pay/cancel?invoice=…`. Neither route existed, so every
// single online payment ended on the 404 catch-all — at the exact moment the
// paying customer had just handed over money. That is the last step of the
// core revenue loop, and it looked broken to the person who mattered most.
//
// Deliberately minimal and self-contained:
//   - The PAYER is not a Worqly user. No login, no tenant branding fetch, and
//     nothing that could 401.
//   - We do NOT confirm payment here. Settlement is decided by the Stripe
//     webhook (POST /api/webhooks/stripe/:slug); this page only reflects where
//     Stripe sent the browser. So the copy says "received", never "paid", and
//     tells them a receipt follows — the truthful thing to say from the
//     browser alone.

import * as React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, XCircle } from 'lucide-react';
import { MarketingPageHead } from '../MarketingPageHead';

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main className="min-h-screen flex items-center justify-center px-6 py-16 bg-pearl-50 dark:bg-ink-900">
      <div className="w-full max-w-md rounded-3xl bg-white/80 dark:bg-white/[0.05] backdrop-blur-xl shadow-card ring-1 ring-ink/[0.08] dark:ring-white/10 p-8 text-center">
        {children}
      </div>
    </main>
  );
}

export function PaySuccessPage() {
  const [params] = useSearchParams();
  const invoice = params.get('invoice');

  return (
    <>
      <MarketingPageHead title="Payment received" description="Your payment has been received." />
      <Shell>
        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-lime-neon/20">
          <CheckCircle2 className="h-7 w-7 text-ink dark:text-white" strokeWidth={1.5} />
        </div>
        <h1 className="text-[22px] font-semibold tracking-[-0.02em] text-ink dark:text-white">
          Payment received
        </h1>
        <p className="mt-3 text-[14px] leading-relaxed text-ink/65 dark:text-white/65">
          Thank you — your payment went through. A receipt is on its way to your email, and the
          invoice will show as paid once your bank confirms the transfer. You can close this page.
        </p>
        {invoice && (
          <p className="mt-4 text-[12px] text-ink/45 dark:text-white/45">
            Reference: <span className="font-mono">{invoice.slice(0, 8)}</span>
          </p>
        )}
        <Link
          to="/"
          className="mt-7 inline-flex items-center justify-center rounded-full bg-ink-900 dark:bg-white px-5 h-10 text-[13px] font-semibold text-white dark:text-ink hover:opacity-90 transition-opacity"
        >
          Done
        </Link>
      </Shell>
    </>
  );
}

export function PayCancelPage() {
  const [params] = useSearchParams();
  const invoice = params.get('invoice');

  return (
    <>
      <MarketingPageHead title="Payment cancelled" description="This payment was not completed." />
      <Shell>
        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-ink/8 dark:bg-white/10">
          <XCircle className="h-7 w-7 text-ink/70 dark:text-white/70" strokeWidth={1.5} />
        </div>
        <h1 className="text-[22px] font-semibold tracking-[-0.02em] text-ink dark:text-white">
          Payment cancelled
        </h1>
        <p className="mt-3 text-[14px] leading-relaxed text-ink/65 dark:text-white/65">
          No payment was taken and you have not been charged. The invoice is still open — you can
          reopen the payment link from the email whenever you are ready.
        </p>
        {invoice && (
          <p className="mt-4 text-[12px] text-ink/45 dark:text-white/45">
            Reference: <span className="font-mono">{invoice.slice(0, 8)}</span>
          </p>
        )}
      </Shell>
    </>
  );
}
